/*
 * (c) copyright 2021 International Road Dynamics Inc. and related companies.
 * All rights reserved.
 * Copyright as an unpublished work.
 *
 * This file contains confidential information of International
 * Road Dynamics Inc. and related companies, and should be treated as proprietary
 * and confidential.
 *
 * The use, copying, disclosure or modification of the
 * programs and/or source code is prohibited unless
 * otherwise provided for in the license or confidentiality
 * agreements.
 *
 * Any copy must contain this copyright notice.
 */

import { ExternalUrlParameterTypes, SiteTypes } from '../constants'

/**
 * Function to get icon dimension (width and height) in pixel by size number (from 1 to 6). If no size number supplied, it's treated as 1.
 *
 * @param {Number} size size number of the icon, from 1 to 6
 *
 * @returns {Object} dimension object containing "width" and "height" in pixel
 */
export function getIconDimensionBySize (size) {
  // Default to smallest
  let width = 44
  let height = 44

  switch (size) {
    case 2:
      width = 48
      height = 48
      break
    case 3:
      width = 52
      height = 52
      break
    case 4:
      width = 56
      height = 56
      break
    case 5:
      width = 60
      height = 60
      break
    case 6:
      width = 64
      height = 64
      break
  }

  return { width, height }
}

/**
 * Function to return icon directory by site status. If status string is not valid, default to "LIVE".
 *
 * @param {string} status site status returned by API. Valid "status" values are:
 * - "LIVE"
 * - "MONITOR"
 * - "NON_FUNCTIONAL"
 * - "CONSTRUCTION"
 * - "DECOMMISSIONED"
 *
 * @returns {string} desired icon URL string
*/
export function getIconUrlByStatus (status) {
  const iconBaseLocal = '/img/pins/'
  let iconName = ''
  switch (status) {
    case 'MONITOR':
      iconName = 'Yellow.png'
      break
    case 'NON_FUNCTIONAL':
      iconName = 'Red.png'
      break
    case 'CONSTRUCTION':
      iconName = 'Orange.png'
      break
    case 'DECOMMISSIONED':
      iconName = 'Grey.png'
      break
    default:
      iconName = 'Green.png'
      break
  }

  return iconBaseLocal + iconName
}

/**
 * Function to return icon directory by site status. If status string is not valid, default to "LIVE".
 *
 * @param {string} status site status returned by API. Valid "status" values are:
 * - "LIVE"
 * - "MONITOR"
 * - "NON_FUNCTIONAL"
 * - "CONSTRUCTION"
 * - "DECOMMISSIONED"
 *
 * @param {string} type site type returned by API. Valid "type" values are:
 * - "CTM"
 * - "EVC"
 * - "WIM"
 *
 * @returns {string} the desired URL string
 */
export function getIconUrlByStatusAndType (status, type) {
  const iconBaseLocal = '/img/pins/'
  let iconName = ''
  switch (status) {
    case 'MONITOR':
      iconName += 'Yellow'
      break
    case 'NON_FUNCTIONAL':
      iconName += 'Red'
      break
    case 'CONSTRUCTION':
      iconName += 'Orange'
      break
    case 'DECOMMISSIONED':
      iconName += 'Grey'
      break
    default:
      iconName += 'Green'
      break
  }

  if (Object.keys(SiteTypes).includes(type)) {
    iconName += type
  }

  iconName += '.png'

  return iconBaseLocal + iconName
}

/**
 * Function to return icon directory by site types.
 *
 * @param {string} type site type returned by API. Valid "type" values are:
 * - "CTM"
 * - "EVC"
 * - "WIM"
 *
 * @returns {string} the desired URL string
 */
export function getIconUrlBySiteType (type) {
  const iconBaseLocal = '/img/pins/'
  const validSites = ['CTM', 'EVC', 'WIM']
  if (validSites.includes(type)) {
    return iconBaseLocal + type + '.png'
  }
  else {
    return null
  }
}

/**
 * Function to get label suffix from array of parameters for VWS external URL
 * Note: if url has multiple label suffix, this function will return the first one.
 *
 * @param {Array} parameters Array of extra parameter objects (e.g. label suffix)
 *
 * @returns {string} first label suffix value of the URL
 */
export function getVwsUrlLabelSuffix (parameters) {
  let suffix
  let suffixParam

  if (parameters) {
    suffixParam = parameters.find((param) => param.parameter === ExternalUrlParameterTypes.LABEL_SUFFIX)
  }

  if (suffixParam) {
    suffix = suffixParam.value
  }

  return suffix
}

/**
 * Function to sort VWS urls by parameters. Currently sorting by label suffix parameter.
 * URL without label suffix are always first.
 * If URL has label suffix, sort by label suffix value.
 *
 * @param {Array} urls Array of VWS URL objects
 *
 * @returns {Array} array of VWS URL objects sorted by label suffix
 */
export function sortVwsUrls (urls) {
  const sortedUrls = urls.slice()

  sortedUrls.sort((a, b) => {
    let aSuffix = getVwsUrlLabelSuffix(a.parameters)
    if (aSuffix === undefined) {
      aSuffix = ''
    }
    let bSuffix = getVwsUrlLabelSuffix(b.parameters)
    if (bSuffix === undefined) {
      bSuffix = ''
    }

    if (aSuffix === bSuffix) {
      return 0
    }
    else if (aSuffix > bSuffix) {
      return 1
    }
    else {
      return -1
    }
  })

  return sortedUrls
}
